/**
 * Low-level procedural geometry helpers for character construction.
 *
 * Everything here returns an INDEXED BufferGeometry carrying exactly
 * `position`, `normal`, `uv` so the parts can be merged into one buffer.
 * Colour, skin indices and skin weights are added later (see skin.js).
 *
 * The workhorse is `loft()`: a swept superellipse tube. Almost every organic
 * or armoured volume in the game — torso, arm, thigh, helmet bowl, boot,
 * collar, sash ring — is a loft with a different profile, which keeps the
 * silhouette fully under our control and the vertex count predictable.
 */
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

const TAU = Math.PI * 2;
const _v = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3();
const _m = new THREE.Matrix4();

/** Signed power that keeps the sign of the base — the superellipse primitive. */
function sp(v, e) { return Math.sign(v) * Math.pow(Math.abs(v), e); }

/**
 * Sweep a closed cross-section along a series of rings.
 *
 * ring = { y, rx, rz, cx=0, cz=0, sq=2, roll=0 }
 *   sq  : superellipse exponent. 2 = ellipse, 3-5 = progressively boxier
 *         (armour plates), 1.4 = pinched/diamond.
 *
 * opts:
 *   seg       radial segments (default 12)
 *   phase     start angle; default -PI/2 so u=0 is the BACK and u=0.5 the FRONT
 *   capTop / capBottom  add a fan cap
 *   vFromY    derive v from world height instead of ring index
 *   uRepeat / vRange
 */
export function loft(rings, opts = {}) {
  const seg = Math.max(3, opts.seg ?? 12);
  const phase = opts.phase ?? -Math.PI / 2;
  const arc = opts.arc ?? TAU;
  const uMap = opts.uMap || null;
  const n = rings.length;
  const cols = seg + 1;
  const capTop = !!opts.capTop, capBottom = !!opts.capBottom;
  const vCount = cols * n + (capTop ? 1 : 0) + (capBottom ? 1 : 0);
  const pos = new Float32Array(vCount * 3);
  const uv = new Float32Array(vCount * 2);
  const idx = [];

  // v parameter along the sweep
  const vs = new Float32Array(n);
  if (opts.vFromY) {
    const y0 = rings[0].y, y1 = rings[n - 1].y;
    for (let j = 0; j < n; j++) vs[j] = y1 === y0 ? j / (n - 1) : (rings[j].y - y0) / (y1 - y0);
  } else {
    for (let j = 0; j < n; j++) vs[j] = j / (n - 1);
  }
  const v0 = opts.vRange ? opts.vRange[0] : 0, v1 = opts.vRange ? opts.vRange[1] : 1;
  const uRep = opts.uRepeat ?? 1;

  let p = 0;
  for (let j = 0; j < n; j++) {
    const r = rings[j];
    const e = 2 / (r.sq ?? 2);
    const roll = r.roll || 0;
    const cr = Math.cos(roll), sr = Math.sin(roll);
    for (let i = 0; i < cols; i++) {
      const t = i / seg;
      const a = phase + t * arc;
      let x = sp(Math.cos(a), e) * r.rx;
      let z = sp(Math.sin(a), e) * r.rz;
      if (roll) { const nx = x * cr - z * sr; z = x * sr + z * cr; x = nx; }
      pos[p * 3] = x + (r.cx || 0);
      pos[p * 3 + 1] = r.y;
      pos[p * 3 + 2] = z + (r.cz || 0);
      uv[p * 2] = uMap ? uMap(t) : t * uRep;
      uv[p * 2 + 1] = v0 + (v1 - v0) * vs[j];
      p++;
    }
  }
  for (let j = 0; j < n - 1; j++) {
    for (let i = 0; i < seg; i++) {
      const a = j * cols + i, b = a + 1, c = a + cols, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  if (capBottom) {
    const c = p;
    const r = rings[0];
    pos[p * 3] = r.cx || 0; pos[p * 3 + 1] = r.y; pos[p * 3 + 2] = r.cz || 0;
    uv[p * 2] = 0.5; uv[p * 2 + 1] = v0; p++;
    for (let i = 0; i < seg; i++) idx.push(c, i, i + 1);
  }
  if (capTop) {
    const c = p;
    const r = rings[n - 1];
    pos[p * 3] = r.cx || 0; pos[p * 3 + 1] = r.y; pos[p * 3 + 2] = r.cz || 0;
    uv[p * 2] = 0.5; uv[p * 2 + 1] = v1; p++;
    const base = (n - 1) * cols;
    for (let i = 0; i < seg; i++) idx.push(c, base + i + 1, base + i);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/**
 * A parametric quad sheet. `fn(u, v, out)` writes a position.
 * Used for capes, coat tails, tassets, sashes and banners.
 * `both:true` duplicates the sheet with flipped winding so it is visible from
 * behind without needing a DoubleSide material (we keep one material total).
 */
export function sheet(fn, nu = 6, nv = 6, opts = {}) {
  const both = opts.both !== false;
  const thick = opts.thickness ?? 0;
  const cols = nu + 1, rows = nv + 1;
  const one = cols * rows;
  const count = one * (both ? 2 : 1);
  const pos = new Float32Array(count * 3);
  const uv = new Float32Array(count * 2);
  const idx = [];
  const uA = opts.uRange ? opts.uRange : [0, 1];
  const vA = opts.vRange ? opts.vRange : [0, 1];
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const u = i / nu, v = j / nv;
      fn(u, v, _v);
      const k = j * cols + i;
      pos[k * 3] = _v.x; pos[k * 3 + 1] = _v.y; pos[k * 3 + 2] = _v.z;
      uv[k * 2] = uA[0] + (uA[1] - uA[0]) * u;
      uv[k * 2 + 1] = vA[0] + (vA[1] - vA[0]) * v;
    }
  }
  for (let j = 0; j < nv; j++) for (let i = 0; i < nu; i++) {
    const a = j * cols + i, b = a + 1, c = a + cols, d = c + 1;
    idx.push(a, c, b, b, c, d);
  }
  if (both) {
    // back face: same shell pushed slightly along its own normal, reversed
    const g0 = new THREE.BufferGeometry();
    g0.setAttribute('position', new THREE.BufferAttribute(pos.slice(0, one * 3), 3));
    g0.setIndex(idx.slice());
    g0.computeVertexNormals();
    const nrm = g0.attributes.normal.array;
    for (let k = 0; k < one; k++) {
      const s = one + k;
      pos[s * 3] = pos[k * 3] - nrm[k * 3] * thick;
      pos[s * 3 + 1] = pos[k * 3 + 1] - nrm[k * 3 + 1] * thick;
      pos[s * 3 + 2] = pos[k * 3 + 2] - nrm[k * 3 + 2] * thick;
      uv[s * 2] = uv[k * 2]; uv[s * 2 + 1] = uv[k * 2 + 1];
    }
    g0.dispose();
    for (let j = 0; j < nv; j++) for (let i = 0; i < nu; i++) {
      const a = one + j * cols + i, b = a + 1, c = a + cols, d = c + 1;
      idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/**
 * A single lamellar plate: a slightly barrel-curved slab. 12 tris.
 * Curved around the Y axis at radius `R` so a row of them wraps a torso.
 */
export function plate(w, h, d, R = 0.3, bevel = 0.22) {
  const g = new THREE.BoxGeometry(w, h, d, 1, 1, 1);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    let x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    // taper the top edge for a rounded lamellar silhouette
    if (y > 0) x *= (1 - bevel);
    // wrap around the cylinder of radius R
    const a = x / Math.max(0.02, R);
    const rr = R + z;
    p.setXYZ(i, Math.sin(a) * rr, y, Math.cos(a) * rr - R);
  }
  g.computeVertexNormals();
  return g;
}

/** Spherical cap — pauldrons, helmet bowls, shield bosses. */
export function capShell(r, phiLen, thetaLen, seg = 10, rings = 5, thetaStart = 0) {
  const g = new THREE.SphereGeometry(r, seg, rings, -phiLen / 2 - Math.PI / 2, phiLen, thetaStart, thetaLen);
  return g;
}

/** Flat extruded silhouette from a 2D point list (crests, blades, plaques). */
export function extrudeFlat(pts, depth, opts = {}) {
  const shape = new THREE.Shape();
  shape.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
  shape.closePath();
  const g = new THREE.ExtrudeGeometry(shape, {
    depth, bevelEnabled: !!opts.bevel, bevelSize: opts.bevelSize ?? 0.004,
    bevelThickness: opts.bevelThickness ?? 0.004, bevelSegments: 1, curveSegments: opts.curveSegments ?? 2,
  });
  g.translate(0, 0, -depth / 2);
  planarUV(g, 'x', 'y');
  return indexed(g);
}

/** Project UVs from the bounding box on two axes (used after extrusion). */
export function planarUV(g, au = 'x', av = 'y') {
  g.computeBoundingBox();
  const bb = g.boundingBox;
  const iu = { x: 0, y: 1, z: 2 }[au], iv = { x: 0, y: 1, z: 2 }[av];
  const min = [bb.min.x, bb.min.y, bb.min.z], max = [bb.max.x, bb.max.y, bb.max.z];
  const du = Math.max(1e-5, max[iu] - min[iu]), dv = Math.max(1e-5, max[iv] - min[iv]);
  const p = g.attributes.position;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const a = [p.getX(i), p.getY(i), p.getZ(i)];
    uv[i * 2] = (a[iu] - min[iu]) / du;
    uv[i * 2 + 1] = (a[iv] - min[iv]) / dv;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return g;
}

/** Guarantee an indexed geometry (required for merging). */
export function indexed(g) {
  if (g.index) return g;
  const m = mergeVertices(g, 1e-5);
  g.dispose();
  return m;
}

/** In-place vertex displacement. fn(x,y,z,out,i) writes the new position. */
export function deform(g, fn) {
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    _v.set(p.getX(i), p.getY(i), p.getZ(i));
    fn(_v.x, _v.y, _v.z, _v, i);
    p.setXYZ(i, _v.x, _v.y, _v.z);
  }
  p.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}

/** transform helper: translate/rotate/scale in one call. */
export function place(g, { pos, rot, scale, order = 'YXZ' } = {}) {
  if (scale) {
    if (typeof scale === 'number') g.scale(scale, scale, scale);
    else g.scale(scale[0], scale[1], scale[2]);
  }
  if (rot) {
    const e = new THREE.Euler(rot[0] || 0, rot[1] || 0, rot[2] || 0, order);
    _m.makeRotationFromEuler(e);
    g.applyMatrix4(_m);
  }
  if (pos) g.translate(pos[0] || 0, pos[1] || 0, pos[2] || 0);
  return g;
}

/** Mirror across X (build one side, get the other free). Winding is fixed. */
export function mirrorX(g) {
  const m = g.clone();
  m.scale(-1, 1, 1);
  const idx = m.index;
  for (let i = 0; i < idx.count; i += 3) {
    const a = idx.getX(i + 1), b = idx.getX(i + 2);
    idx.setX(i + 1, b); idx.setX(i + 2, a);
  }
  idx.needsUpdate = true;
  m.computeVertexNormals();
  return m;
}

/** Remap the [0,1] uv of a geometry into an atlas tile rect (uv space). */
export function tileUV(g, rect, opts = {}) {
  const uv = g.attributes.uv;
  if (!uv) return g;
  const inset = opts.inset ?? 0.0015;
  const x = rect.u + inset, y = rect.v + inset;
  const w = rect.w - inset * 2, h = rect.h - inset * 2;
  const su = opts.su ?? 1, sv = opts.sv ?? 1, ou = opts.ou ?? 0, ov = opts.ov ?? 0;
  for (let i = 0; i < uv.count; i++) {
    let u = uv.getX(i) * su + ou, v = uv.getY(i) * sv + ov;
    if (opts.wrap) { u = u - Math.floor(u); v = v - Math.floor(v); }
    else { u = u < 0 ? 0 : u > 1 ? 1 : u; v = v < 0 ? 0 : v > 1 ? 1 : v; }
    uv.setXY(i, x + u * w, y + v * h);
  }
  uv.needsUpdate = true;
  return g;
}

/** Flat vertex colour (linear space — pass a THREE.Color). */
export function paint(g, color, fn) {
  const p = g.attributes.position;
  const c = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) {
    let r = color.r, gr = color.g, b = color.b;
    if (fn) {
      const k = fn(p.getX(i), p.getY(i), p.getZ(i), i);
      r *= k; gr *= k; b *= k;
    }
    c[i * 3] = r; c[i * 3 + 1] = gr; c[i * 3 + 2] = b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(c, 3));
  return g;
}

export function triCount(g) { return (g.index ? g.index.count : g.attributes.position.count) / 3; }

export function mergeAll(list) {
  const clean = list.filter(Boolean).map(indexed);
  const m = mergeGeometries(clean, false);
  for (const g of clean) g.dispose();
  return m;
}

export { mergeGeometries, mergeVertices };
